/**
 * Bake watermark marks onto an export canvas (when embed is enabled).
 */

import {
  getWatermarkFontStack,
  measureWatermarkText,
  watermarkFontCssWeight,
  watermarkFontCssStyle,
  watermarkFontSizePx,
  watermarkTextBoxLayout,
  watermarkLogoDisplaySize,
  watermarkTilePositions,
  WATERMARK_CONTENT_TYPES,
} from "../features/tools/watermark/Popup/watermarkSettings";
import {
  getWatermarkLogoCropRect,
  watermarkLogoCropSourcePixels,
} from "../features/tools/watermark/Editor/watermarkImageFormatAdapter";

function loadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing logo URL"));
      return;
    }
    const img = new Image();
    if (url.startsWith("http://") || url.startsWith("https://")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load logo: ${url}`));
    img.src = url;
  });
}

function applyTextEffect(ctx, effect, color, fontSize) {
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.lineWidth = 0;

  switch (effect) {
    case "shadow":
      ctx.shadowColor = "rgba(0,0,0,0.55)";
      ctx.shadowBlur = Math.max(2, fontSize * 0.15);
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
      break;
    case "glow":
      ctx.shadowColor = color;
      ctx.shadowBlur = Math.max(4, fontSize * 0.35);
      break;
    case "outline":
      ctx.lineWidth = Math.max(1, fontSize * 0.06);
      ctx.strokeStyle = color === "#FFFFFF" ? "#111111" : "#ffffff";
      break;
    default:
      break;
  }
}

/**
 * Draw watermark onto ctx in stage/export pixel space.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ watermark: object, stageWidth: number, stageHeight: number, forceBake?: boolean }} opts
 */
export async function drawWatermarkOnCtx(
  ctx,
  { watermark, stageWidth, stageHeight, forceBake = false }
) {
  if (!ctx || !watermark?.enabled) return;
  if (!forceBake && !watermark?.embed) return;
  if (!stageWidth || !stageHeight) return;

  const stageBasis = Math.min(
    Math.max(1, Number(stageWidth) || 1),
    Math.max(1, Number(stageHeight) || 1)
  );
  const resolveMarkFontSizePx = () => {
    const norm = Number(watermark.fontSizeNorm);
    if (Number.isFinite(norm) && norm > 0) {
      return Math.max(1, norm * stageBasis);
    }
    const absolutePx = Number(watermark.fontSizePx);
    if (Number.isFinite(absolutePx) && absolutePx > 0) return absolutePx;
    return watermarkFontSizePx(watermark.size, stageWidth, stageHeight);
  };

  const isLogoMode =
    watermark.contentType === WATERMARK_CONTENT_TYPES.LOGO &&
    Boolean(watermark.logoUrl);
  const hasText = Boolean(String(watermark.text || "").trim());
  if (!isLogoMode && !hasText) return;

  const rotation = Number(watermark.rotation) || 0;
  const offsetX = Number(watermark.offsetX) || 0;
  const offsetY = Number(watermark.offsetY) || 0;
  const opacityRaw = Number(watermark.opacity);
  const opacity =
    (Number.isFinite(opacityRaw)
      ? Math.max(0, Math.min(100, opacityRaw))
      : 100) / 100;

  let markW;
  let markH;
  let fontSizePx = 12;
  let fontFamily = getWatermarkFontStack(watermark.fontId);
  let logoImg = null;
  let cropRect = null;
  let naturalW = 0;
  let naturalH = 0;

  if (isLogoMode) {
    try {
      logoImg = await loadImage(watermark.logoUrl);
    } catch (err) {
      throw new Error(
        err?.message || "Could not load signature image for PDF export."
      );
    }
    naturalW =
      watermark.logoNaturalWidth || logoImg.naturalWidth || logoImg.width;
    naturalH =
      watermark.logoNaturalHeight || logoImg.naturalHeight || logoImg.height;
    cropRect = getWatermarkLogoCropRect(watermark);
    const resolvedPx = resolveMarkFontSizePx();
    if (resolvedPx > 0) {
      let aspect = naturalW > 0 && naturalH > 0 ? naturalW / naturalH : 1;
      if (cropRect && typeof cropRect === "object") {
        const cw = Math.max(0.05, Number(cropRect.w) || 1);
        const ch = Math.max(0.05, Number(cropRect.h) || 1);
        aspect = (naturalW > 0 && naturalH > 0 ? naturalW / naturalH : 1) * (cw / ch);
      }
      markH = Math.max(4, resolvedPx);
      markW = Math.max(4, markH * aspect);
    } else {
      const size = watermarkLogoDisplaySize(
        watermark.size,
        stageWidth,
        stageHeight,
        naturalW,
        naturalH,
        cropRect
      );
      markW = size.width;
      markH = size.height;
    }
  } else {
    fontSizePx = resolveMarkFontSizePx();
    const metrics = measureWatermarkText(
      watermark.text,
      fontSizePx,
      fontFamily,
      watermark.fontWeight,
      {
        letterSpacing: watermark.letterSpacing,
        textScript: watermark.textScript,
        fontStyle: watermark.fontStyle,
      }
    );
    markW = metrics.width;
    markH = metrics.height;
  }

  const tileMarks = watermarkTilePositions({
    tile: watermark.tile || "single",
    spacing: watermark.spacing,
    stageWidth,
    stageHeight,
    textWidth: markW,
    textHeight: markH,
    fontSizePx: isLogoMode ? Math.max(markW, markH) : fontSizePx,
    rotationDeg: rotation,
    position: watermark.position || "center",
    margin: watermark.margin,
    offsetX,
    offsetY,
    flushEdges: watermark.flushEdges !== false,
  });

  const marks = tileMarks.map((m) => ({
    x: m.x * stageWidth,
    y: m.y * stageHeight,
  }));

  const color = watermark.color || "#FFFFFF";
  const effect = watermark.effect || "none";
  const text = String(watermark.text || "");

  for (const pos of marks) {
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.globalAlpha = opacity;

    if (isLogoMode && logoImg) {
      const crop = watermarkLogoCropSourcePixels(cropRect, naturalW, naturalH);
      const tint = watermark.logoTintColor || null;
      const logoColor = tint || "#FFFFFF";

      const off = document.createElement("canvas");
      off.width = Math.max(1, Math.ceil(markW));
      off.height = Math.max(1, Math.ceil(markH));
      const octx = off.getContext("2d");
      if (octx) {
        octx.drawImage(
          logoImg,
          crop.sx,
          crop.sy,
          crop.sw,
          crop.sh,
          0,
          0,
          off.width,
          off.height
        );
        if (tint) {
          octx.globalCompositeOperation = "source-in";
          octx.fillStyle = tint;
          octx.fillRect(0, 0, off.width, off.height);
          octx.globalCompositeOperation = "source-over";
        }
      }

      applyTextEffect(ctx, effect, logoColor, Math.max(markW, markH) * 0.08);
      if (effect === "outline") {
        const stroke =
          String(logoColor).toUpperCase() === "#FFFFFF" ? "#111111" : "#ffffff";
        ctx.shadowColor = stroke;
        ctx.shadowBlur = 0;
        const offsets = [
          [1, 0],
          [-1, 0],
          [0, 1],
          [0, -1],
          [1, 1],
          [-1, -1],
        ];
        for (const [ox, oy] of offsets) {
          ctx.shadowOffsetX = ox;
          ctx.shadowOffsetY = oy;
          ctx.drawImage(off, -markW / 2, -markH / 2, markW, markH);
        }
        ctx.shadowColor = "transparent";
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
      ctx.drawImage(off, -markW / 2, -markH / 2, markW, markH);
    } else {
      ctx.font = `${watermarkFontCssStyle(watermark.fontStyle)} ${watermarkFontCssWeight(watermark.fontWeight)} ${fontSizePx}px ${fontFamily}`;
      // Canvas has no real "justify". Preview centers the mark box (translate -50%),
      // so justify must bake as center — otherwise left-edge flush shifts ~half text width.
      const rawAlign = watermark.textAlign || "center";
      const align =
        rawAlign === "justify" || rawAlign === "center" ? "center" : rawAlign;
      const layout = watermarkTextBoxLayout(align, { width: markW });
      const boxW = layout.width || markW;
      let textX = 0;
      if (align === "left") textX = -boxW / 2;
      else if (align === "right") textX = boxW / 2;
      ctx.textAlign = align;
      ctx.textBaseline = "middle";
      ctx.fillStyle = color;
      applyTextEffect(ctx, effect, color, fontSizePx);
      if (effect === "outline") {
        ctx.strokeText(text, textX, 0);
      }
      ctx.fillText(text, textX, 0);
    }

    ctx.restore();
  }
}
